import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  const { t } = useTranslation()

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const handlePageClick = (page: number) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page)
    }
  }

  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 7

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i)
        }
        pages.push('ellipsis')
        pages.push(totalPages - 1)
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push(2)
        pages.push('ellipsis')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        pages.push(2)
        pages.push('ellipsis')
        pages.push(currentPage - 1)
        pages.push(currentPage)
        pages.push(currentPage + 1)
        pages.push('ellipsis')
        pages.push(totalPages - 1)
        pages.push(totalPages)
      }
    }

    return pages
  }

  const pageNumbers = getPageNumbers()

  if (totalPages <= 1) {
    return null
  }

  return (
    <div className='inline-flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white'>
      <Button
        type='button'
        variant='ghost'
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className={cn(
          'px-4 py-2 h-auto rounded-none border-r border-gray-300 font-bold text-gray-700',
          'hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed',
          'bg-white'
        )}
      >
        <ChevronLeft className='h-4 w-4 mr-2' />
        {t('common.previous', 'Previous')}
      </Button>

      {pageNumbers.map((page, index) => {
        if (page === 'ellipsis') {
          return (
            <Button
              key={`ellipsis-${index}`}
              type='button'
              variant='ghost'
              disabled
              className={cn(
                'px-4 py-2 h-auto rounded-none border-r border-gray-300 font-bold text-gray-700',
                'cursor-default hover:bg-transparent bg-white'
              )}
            >
              ...
            </Button>
          )
        }

        const pageNum = page as number
        const isActive = pageNum === currentPage

        return (
          <Button
            key={pageNum}
            type='button'
            variant='ghost'
            onClick={() => handlePageClick(pageNum)}
            className={cn(
              'px-4 py-2 h-auto rounded-none border-r border-gray-300 font-bold min-w-[44px]',
              'hover:bg-gray-50',
              isActive ? 'bg-gray-100 text-gray-900' : 'text-gray-700 bg-white'
            )}
          >
            {pageNum}
          </Button>
        )
      })}

      <Button
        type='button'
        variant='ghost'
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className={cn(
          'px-4 py-2 h-auto rounded-none font-bold text-gray-700',
          'hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed',
          'bg-white'
        )}
      >
        {t('common.next', 'Next')}
        <ChevronRight className='h-4 w-4 ml-2' />
      </Button>
    </div>
  )
}
